import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangeLanguageApi } from '@hero/api-hooks';
import type { Language } from '@hero/schema';
import { useChangeLanguageConfig } from './ChangeLanguageConfigProvider';
import { isLoggedIn, saveTokens } from './tokens';

const applyLanguageLocally = (langCode: string) => {
  document.documentElement.lang = langCode;
  document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';

  if (langCode === 'ar') {
    document.body.classList.add('font-almarai');
    document.body.classList.remove('font-inter');
  } else {
    document.body.classList.add('font-inter');
    document.body.classList.remove('font-almarai');
  }

  localStorage.setItem('language', langCode);
  localStorage.setItem('direction', langCode === 'ar' ? 'rtl' : 'ltr');
};

export type LanguageInfo = {
  code: Language;
  nativeName: string;
  shortName: string;
  emoji: string;
  direction: 'ltr' | 'rtl';
};

export const LANGUAGES: LanguageInfo[] = [
  { code: 'en', nativeName: 'English', shortName: 'EN', emoji: '🇺🇸', direction: 'ltr' },
  { code: 'ar', nativeName: 'العربية', shortName: 'AR', emoji: '🇸🇦', direction: 'rtl' },
];

const getLang = (code: Language) => LANGUAGES.find(l => l.code === code) || LANGUAGES[0];

export const useChangeLanguageManager = () => {
  const { reloadOnChange } = useChangeLanguageConfig();
  const { mutateAsync } = useChangeLanguageApi();
  const { i18n } = useTranslation();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(saved);
    applyLanguageLocally(saved);
  }, [i18n]);

  const changeLanguage = useCallback(
    async (language: Language) => {
      const currentLang = (i18n.language || 'en') as Language;
      if (language === currentLang) return;

      setIsChangingLanguage(true);
      try {
        if (isLoggedIn()) {
          const result = await mutateAsync({ language });
          saveTokens(result);
        }

        i18n.changeLanguage(language);
        applyLanguageLocally(language);

        if (reloadOnChange) {
          window.location.reload();
          return;
        }
      } catch (error) {
        console.error('Failed to change language:', error);
      } finally {
        setIsChangingLanguage(false);
      }
    },
    [mutateAsync, i18n, reloadOnChange],
  );

  const current = getLang((i18n.language || 'en') as Language);
  const isRtl = current.direction === 'rtl';

  return { changeLanguage, isChangingLanguage, current, languages: LANGUAGES, isRtl };
};
